import { onReducedMotionChange, prefersReducedMotion } from "../helpers/reducedMotion.js";

// The animated ridge lines behind /trial-started/ (#238), ported from the "Check Your
// Email" design's canvas. Stacked rows of noise, swelling toward the middle of the screen
// and pulsing slowly, each row filled with the background so it hides the rows behind it.
//
// Pure decoration: the canvas is aria-hidden and pointer-events: none, and the page reads
// the same without it. Under reduced motion it draws one still frame instead of a loop.

// $trial-bg in 03-modules/_trial-started.scss. Each row is filled with it to hide the rows
// behind, so it has to match the page behind the canvas exactly.
const BG = "#050607";
// $teal in 00-sass/_variables.scss, as an rgb triple for the per-row alpha.
const LINE_RGB = "95, 255, 207";

// The ridges redraw at most FPS times a second, whatever the display's refresh rate. The
// pulse is slow enough that 30 looks the same as 60 or 120, and without a GPU canvas
// every redraw is rasterised on the CPU. Uncapped, a CPU fast enough to keep up drew at
// the display's full rate (90-120fps headless) and never let the page go idle: Chrome's
// network-idle signal took 8-15s at 1024x1366 and never came at 1600x1200, which timed
// the viewport suite out on CI (#242). Capped, it comes in about a second.
//
// The 2ms slack keeps a 60Hz display, whose frames land a hair either side of 1000 / 60,
// drawing every second frame rather than every third.
const FPS = 30;
const FRAME_MS = 1000 / FPS - 2;

// The loop also watches how long each redraw holds the main thread, and a device that
// could not redraw MIN_FPS times a second uncapped gets a still frame instead, as under
// reduced motion. On a CPU without GPU canvas almost all of a frame's cost is rasterising
// the canvas, not the script — draw() itself stays under 8ms even at 6x CPU throttle — so
// timing draw() would miss it. What does show it is the gap from a redraw to the very
// next callback: one vsync with headroom, longer when the raster spills past it. The rate
// actually drawn cannot: under the cap, a device with headroom and one saturated at 30
// both draw 30 (at 2x throttle and 1600x1200, 22fps drawn kept the page from ever going
// idle).
//
// The gap comes in whole vsyncs: on a 60Hz display, 16.7ms with headroom and 33.3ms when
// the raster spills past one. The window averages them, and reads under MIN_FPS once the
// mean passes 25ms: more than half its redraws spilling, not any one of them. A genuine
// 30Hz display reads 30fps on every redraw, and gets the still frame.
//
// Measured over WINDOW_MS windows, and only two slow windows in a row stop it, so load-time
// jank on a capable machine (fonts, the HubSpot scripts) cannot trip it on its own. Each
// gap counts for at most MAX_GAP_MS, so one long task reads as one slow redraw rather than
// its full length; a device that is slow throughout still reads slow, since the cap is
// twice the 25ms the mean is held to.
const MIN_FPS = 40;
const WINDOW_MS = 1500;
const MAX_GAP_MS = 50;

class RidgeField {
    constructor(canvas) {
        this.canvas = canvas;
        this.ctx = canvas.getContext("2d");
        this.raf = null;
        this.resizeRaf = null;
        this.t0 = performance.now();
        this.width = 0;
        this.height = 0;
        this.dpr = 0;
        this.lastDraw = -Infinity;
        // The time of the frame on screen, so a resize can put it back.
        this.t = 0;
        // Frame-cost tracking: see MIN_FPS. `drew` marks the callback after a redraw, the
        // one that times it. `tooSlow` is permanent for the page view.
        this.drew = false;
        this.windowStart = 0;
        this.windowDraws = 0;
        this.windowCost = 0;
        this.slowWindows = 0;
        this.tooSlow = false;

        if (!this.ctx) return;

        this.resize();
        window.addEventListener("resize", this.handleResize);
        // A hidden tab gets no frames, so the window spanning it would read as one long,
        // slow stretch. Start a fresh one instead. This is the event and not a gap
        // threshold on frame timestamps: on the slowest devices a single frame can take
        // longer than any threshold that would also catch a short tab switch. For the same
        // reason the redraw before it goes untimed.
        document.addEventListener("visibilitychange", () => {
            this.windowStart = 0;
            this.drew = false;
        });
        onReducedMotionChange(this.handleMotionPreference);
        this.handleMotionPreference();
    }

    // Returns false when nothing changed. Assigning canvas.width/height frees, reallocates
    // and clears the backing store (about 20MB at 2880x1800) even at the same size, and iOS
    // fires resize with no size change whenever the URL bar shows or hides.
    resize() {
        // Capped at 2x: a 3x phone would more than double the fill cost for lines nobody
        // can tell apart at that density.
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const width = this.canvas.clientWidth;
        const height = this.canvas.clientHeight;

        if (width === this.width && height === this.height && dpr === this.dpr) return false;

        this.width = width;
        this.height = height;
        this.dpr = dpr;
        this.canvas.width = width * dpr;
        this.canvas.height = height * dpr;
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        this.layout();

        return true;
    }

    // Everything that depends only on the canvas size, worked out once per resize rather
    // than once per point per frame: the row grid, each column's x, and each column's
    // envelope — the bell curve that swells the ridges toward the middle of the screen.
    layout() {
        const { width: W, height: H } = this;
        const cols = Math.max(80, Math.round(W / 8));

        this.rows = Math.max(26, Math.round(H / 18));
        this.top = H * 0.08;
        this.step = (H * 0.9) / this.rows;
        this.xs = new Float64Array(cols + 1);
        this.envs = new Float64Array(cols + 1);

        for (let k = 0; k <= cols; k++) {
            const x = (k / cols) * W;
            const u = (x - W / 2) / (W * 0.24);

            this.xs[k] = x;
            this.envs[k] = Math.exp(-u * u * 0.9);
        }
    }

    // Coalesced to one pass per frame: a window drag fires dozens of these a second.
    handleResize = () => {
        if (this.resizeRaf) return;

        this.resizeRaf = requestAnimationFrame((now) => {
            this.resizeRaf = null;

            // A real resize clears the canvas, so it is redrawn here rather than left blank
            // until the loop's next redraw, which the cap can put up to a frame away. The
            // redraw counts toward the cap but goes untimed: reallocating the canvas is not
            // what MIN_FPS measures.
            if (!this.resize()) return;

            if (this.raf) {
                this.lastDraw = now;
                this.drew = false;
                this.redraw((now - this.t0) / 1000);
            } else {
                this.redraw(this.t);
            }
        });
    };

    handleMotionPreference = () => {
        cancelAnimationFrame(this.raf);
        this.raf = null;

        if (prefersReducedMotion() || this.tooSlow) {
            this.redraw(0);
        } else {
            this.windowStart = 0;
            this.drew = false;
            this.raf = requestAnimationFrame(this.frame);
        }
    };

    frame = (now) => {
        // The callback after a redraw; see MIN_FPS.
        if (this.drew) {
            this.drew = false;
            this.windowDraws++;
            this.windowCost += Math.min(now - this.lastDraw, MAX_GAP_MS);
        }

        // Not yet time for the next redraw; see FPS.
        if (now - this.lastDraw < FRAME_MS) {
            this.raf = requestAnimationFrame(this.frame);
            return;
        }

        this.lastDraw = now;
        this.drew = true;
        this.redraw((now - this.t0) / 1000);

        if (this.keepingUp(now)) {
            this.raf = requestAnimationFrame(this.frame);
        } else {
            // Whatever frame is on screen stays as the still.
            this.tooSlow = true;
            this.raf = null;
        }
    };

    // False once two windows in a row come in under MIN_FPS.
    keepingUp(now) {
        if (!this.windowStart) {
            this.windowStart = now;
            this.windowDraws = 0;
            this.windowCost = 0;
            return true;
        }

        if (now - this.windowStart < WINDOW_MS) return true;

        // The rate the device could redraw at uncapped. A window with no timed redraws
        // (only possible straight after a resize) counts as keeping up.
        const slow = this.windowCost > 0 && (this.windowDraws * 1000) / this.windowCost < MIN_FPS;

        this.slowWindows = slow ? this.slowWindows + 1 : 0;
        this.windowStart = now;
        this.windowDraws = 0;
        this.windowCost = 0;

        return this.slowWindows < 2;
    }

    redraw(t) {
        this.t = t;
        this.draw(t);
    }

    draw(t) {
        const { ctx, width: W, height: H, rows, top, step, xs, envs } = this;
        const last = xs.length - 1;

        ctx.fillStyle = BG;
        ctx.fillRect(0, 0, W, H);
        ctx.lineWidth = 1.2;

        for (let r = 0; r < rows; r++) {
            const y0 = top + r * step;
            const seed = r * 1.7;
            // The row's pulse depends only on the row and the time, not the column.
            const beat = 0.75 + 0.25 * Math.sin(t * 2.4 + r * 0.35);
            const ridge = new Path2D();

            ridge.moveTo(0, y0);

            for (let k = 0; k <= last; k++) {
                const x = xs[k];
                const env = envs[k];
                const n =
                    Math.sin(x * 0.021 + seed + t * 1.3) * 0.5 +
                    Math.sin(x * 0.047 - seed * 1.3 + t * 2.1) * 0.3 +
                    Math.sin(x * 0.11 + seed * 0.7 - t * 3.2) * 0.2;

                ridge.lineTo(x, y0 - Math.abs(n) * step * 4.2 * env * beat - env * step * 0.6);
            }

            // The fill only has to hide the rows already drawn, and every ridge sits at or
            // above its own baseline (both terms subtracted above are never negative), so
            // nothing of an earlier row reaches below this row's y0. Closing one step under
            // it, rather than at the bottom of the canvas, gives the same picture for a
            // fraction of the fill: before, every row painted most of the lower canvas.
            //
            // The ridge is stroked on its own, without the closing edges, which would
            // otherwise draw a line across the screen at y0 + step.
            const body = new Path2D(ridge);

            body.lineTo(W, y0 + step);
            body.lineTo(0, y0 + step);
            body.closePath();
            ctx.fillStyle = BG;
            ctx.fill(body);

            // Brightest through the middle rows, dimmer toward the top and bottom.
            const mid = 1 - Math.abs(r / rows - 0.5) * 2;

            ctx.strokeStyle = `rgba(${LINE_RGB}, ${0.18 + mid * 0.42})`;
            ctx.stroke(ridge);
        }
    }
}

document.querySelectorAll("[data-ridge-field]").forEach((canvas) => new RidgeField(canvas));
